import React, { useEffect, useId, useRef, useState } from 'react';
import { Badge, Icon, SearchInput, cx } from './ui';

// ── ScopePicker ────────────────────────────────────────────────────────────
// Controlled scope chooser. The `value` array of permission ids is the single
// source of truth; we never keep a local "selected" copy. What IS local is
// presentation only: which groups are expanded, and the filter text.
//
// Catalog shape (from GET /api/api-keys/scopes, rendered verbatim, so this file
// hardcodes no scope id):
//   [{ id, label, description, permissions: [{ id, label, endpoint }] }, ...]
//
// Props:
//   value, onChange, catalog  as above
//   maxHeight   cap on the group list, px or any CSS length. The list scrolls
//               inside it, so the filter stays in view and a dialog holding the
//               picker still fits a laptop screen with every group expanded.
//   labelledBy  id of the visible label that names the picker

const lower = (s) => String(s || '').toLowerCase();

export default function ScopePicker({ value = [], onChange, catalog = [], maxHeight = 320, labelledBy }) {
  const uid = useId();
  const [query, setQuery] = useState('');
  // Browse mode remembers the groups the operator opened. Filter mode opens
  // every matching group, so it only has to remember the ones they closed.
  const [open, setOpen] = useState(() => new Set());
  const [closedWhileFiltering, setClosedWhileFiltering] = useState(() => new Set());
  const selected = new Set(value || []);
  const q = query.trim().toLowerCase();
  const filtering = q.length > 0;

  const togglePermission = (permId) => {
    const next = new Set(selected);
    if (next.has(permId)) next.delete(permId);
    else next.add(permId);
    onChange(Array.from(next));
  };

  const setBulk = (permIds, enable) => {
    const next = new Set(selected);
    for (const pid of permIds) {
      if (enable) next.add(pid);
      else next.delete(pid);
    }
    onChange(Array.from(next));
  };

  if (!catalog || catalog.length === 0) {
    return (
      <div className="row gap-2 text-sm text-muted" role="status">
        <span className="spinner-sm" aria-hidden="true" /> Loading scopes…
      </div>
    );
  }

  // A group whose own name or description matches keeps all of its scopes;
  // otherwise only the scopes whose label, id or endpoint match are listed.
  const groups = catalog
    .map((set) => {
      const perms = set.permissions || [];
      if (!filtering) return { set, perms };
      const groupHit = lower(set.label).includes(q) || lower(set.description).includes(q);
      return {
        set,
        perms: groupHit ? perms : perms.filter((p) => [p.label, p.id, p.endpoint].some((s) => lower(s).includes(q))),
      };
    })
    .filter((g) => !filtering || g.perms.length > 0);

  const flip = (id) => (prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };
  const isExpanded = (id) => (filtering ? !closedWhileFiltering.has(id) : open.has(id));
  const toggleGroup = (id) => (filtering ? setClosedWhileFiltering(flip(id)) : setOpen(flip(id)));
  const allOpen = catalog.every((set) => open.has(set.id));
  const toggleAll = () => setOpen(allOpen ? new Set() : new Set(catalog.map((set) => set.id)));

  const filter = (v) => {
    setQuery(v);
    setClosedWhileFiltering(new Set());
  };

  return (
    <div className="stack gap-2" role="group" aria-labelledby={labelledBy}>
      <div className="row gap-2">
        <SearchInput
          value={query}
          onChange={filter}
          placeholder="Filter by name, scope or endpoint…"
          label="Filter scopes"
          className="grow"
          // Escape clears a filter before it closes the dialog around the picker.
          // Modal listens on window, and a keypress that only meant "clear this"
          // should not throw away a half-filled form.
          onKeyDown={(e) => {
            if (e.key === 'Escape' && query) {
              e.stopPropagation();
              filter('');
            }
          }}
        />
        {!filtering && (
          <button type="button" className="btn btn-ghost btn-sm shrink-0" onClick={toggleAll}>
            <Icon name={allOpen ? 'chevronUp' : 'chevronDown'} />
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
        )}
      </div>

      <div className="card-inset" style={{ maxHeight, overflowY: 'auto', paddingBlock: 'var(--sp-1)' }}>
        {groups.length === 0 ? (
          <p className="empty-inline">No scopes match “{query.trim()}”.</p>
        ) : groups.map(({ set, perms }) => (
          <ScopeGroup
            key={set.id}
            set={set}
            perms={perms}
            selected={selected}
            filtering={filtering}
            expanded={isExpanded(set.id)}
            baseId={`${uid}-${set.id}`}
            onToggleOpen={() => toggleGroup(set.id)}
            onTogglePermission={togglePermission}
            onSetBulk={setBulk}
          />
        ))}
      </div>
    </div>
  );
}

// ── ScopeGroup ─────────────────────────────────────────────────────────────
// The header is the expand control, as the whole header row always was; the
// checkbox beside it is the only thing that bulk-selects. On a consent surface
// the large target should be the harmless one.
function ScopeGroup({
  set, perms, selected, filtering, expanded, baseId,
  onToggleOpen, onTogglePermission, onSetBulk,
}) {
  const masterRef = useRef(null);
  const listed = perms.map((p) => p.id);
  const all = (set.permissions || []).map((p) => p.id);
  const chosen = all.filter((p) => selected.has(p)).length;
  // The group box acts on what is LISTED. While a filter hides part of a group,
  // ticking it must not grant scopes the operator cannot see.
  const shown = listed.filter((p) => selected.has(p)).length;
  const allChecked = listed.length > 0 && shown === listed.length;
  const someChecked = shown > 0 && shown < listed.length;
  const labelId = `${baseId}-label`;
  const panelId = `${baseId}-panel`;

  // Indeterminate is not exposed via JSX; we have to poke the DOM node.
  useEffect(() => {
    if (masterRef.current) {
      masterRef.current.indeterminate = someChecked;
    }
  }, [someChecked]);

  return (
    <section className="channel-item" aria-labelledby={labelId}>
      <div className="row row-top gap-3">
        <input
          ref={masterRef}
          type="checkbox"
          checked={allChecked}
          // Tri-state: a partial group turns fully on; a full one turns off.
          onChange={() => onSetBulk(listed, !allChecked)}
          aria-label={filtering ? `Select the listed ${set.label} scopes` : `Select all ${set.label} scopes`}
          style={{ marginTop: 2 }}
        />
        <button
          type="button"
          className="grow stack gap-1"
          style={{ textAlign: 'left' }}
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={onToggleOpen}
        >
          <span className="row gap-2">
            <span id={labelId} className="fw-600 text-strong">{set.label}</span>
            <Badge tone={chosen > 0 ? 'accent' : 'neutral'}>
              {chosen}/{all.length}<span className="sr-only"> selected</span>
            </Badge>
            <Icon name={expanded ? 'chevronUp' : 'chevronDown'} className="ml-auto text-muted" />
          </span>
          {set.description && <span className="text-sm text-muted">{set.description}</span>}
        </button>
      </div>

      {/* Rendered even when collapsed so `aria-controls` always points at
          something; `.hidden` also takes it out of the dialog's Tab order. */}
      <div id={panelId} className={cx('stack gap-2 mt-3', !expanded && 'hidden')} style={{ paddingLeft: 28 }}>
        {perms.map((p) => (
          <label key={p.id} className="check-row">
            <input
              type="checkbox"
              checked={selected.has(p.id)}
              onChange={() => onTogglePermission(p.id)}
            />
            <span className="min-w-0">
              {p.label}
              <span className="check-hint">
                <span className="mono">{p.id}</span>
                {p.endpoint && <> · <span className="mono break">{p.endpoint}</span></>}
              </span>
            </span>
          </label>
        ))}
      </div>
    </section>
  );
}
